import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';


const PaymentMethodSelector = ({ selectedMethod, onMethodSelect, walletBalance = 0 }) => {
  const [expandedMethod, setExpandedMethod] = useState(null);

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('fr-CM', {
      style: 'currency',
      currency: 'XAF',
      minimumFractionDigits: 0
    })?.format(amount);
  };

  const paymentMethods = [
    {
      id: 'orange_money',
      name: 'Orange Money',
      icon: 'Smartphone',
      description: 'Paiement via Orange Money',
      processingFee: '1.5%',
      minAmount: 100,
      maxAmount: 2000000,
      estimatedTime: '2-5 minutes',
      features: ['Instant transfer', 'SMS confirmation', 'No registration required'],
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200'
    },
    {
      id: 'mtn_momo',
      name: 'MTN Mobile Money',
      icon: 'Smartphone',
      description: 'Paiement via MTN MoMo',
      processingFee: '1.5%',
      minAmount: 100,
      maxAmount: 2000000,
      estimatedTime: '2-5 minutes',
      features: ['Instant transfer', 'USSD confirmation', 'Wide coverage'],
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
      borderColor: 'border-yellow-200'
    },
    {
      id: 'card',
      name: 'Visa/Mastercard',
      icon: 'CreditCard',
      description: 'Carte bancaire internationale',
      processingFee: '2.5%',
      minAmount: 500,
      maxAmount: 5000000,
      estimatedTime: '1-3 minutes',
      features: ['Secure 3D authentication', 'International cards', 'Instant processing'],
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200'
    },
    {
      id: 'wallet',
      name: 'AutoCasse Wallet',
      icon: 'Wallet',
      description: 'Portefeuille interne',
      processingFee: 'Free',
      minAmount: 0,
      maxAmount: walletBalance,
      estimatedTime: 'Instant',
      features: ['No fees', 'Instant payment', 'Secure balance'],
      color: 'text-primary',
      bgColor: 'bg-primary/5',
      borderColor: 'border-primary/20',
      balance: walletBalance
    }
  ];

  const handleMethodSelect = (method) => {
    onMethodSelect(method);
    setExpandedMethod(expandedMethod === method?.id ? null : method?.id);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center space-x-2 mb-6">
        <Icon name="CreditCard" size={20} className="text-primary" />
        <h2 className="text-lg font-semibold text-foreground">Payment Method</h2>
      </div>
      {paymentMethods?.map((method) => (
        <div key={method?.id} className="border border-border rounded-lg overflow-hidden">
          <button
            onClick={() => handleMethodSelect(method)}
            className={`w-full p-4 text-left transition-smooth hover:bg-muted/50 ${
              selectedMethod?.id === method?.id ? `${method?.bgColor} ${method?.borderColor}` : 'bg-card'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${method?.bgColor}`}>
                  <Icon name={method?.icon} size={20} className={method?.color} />
                </div>
                <div>
                  <h3 className="font-medium text-foreground">{method?.name}</h3>
                  <p className="text-sm text-muted-foreground">{method?.description}</p>
                  {method?.id === 'wallet' && (
                    <p className="text-sm font-medium text-primary">
                      Balance: {formatAmount(method?.balance)}
                    </p>
                  )}
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <div className="text-right">
                  <div className="text-sm font-medium text-foreground">
                    Fee: {method?.processingFee}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {method?.estimatedTime}
                  </div>
                </div>
                
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  selectedMethod?.id === method?.id 
                    ? `${method?.borderColor} ${method?.bgColor}` 
                    : 'border-border bg-background'
                }`}>
                  {selectedMethod?.id === method?.id && (
                    <div className={`w-2 h-2 rounded-full ${method?.color?.replace('text-', 'bg-')}`}></div>
                  )}
                </div>
              </div>
            </div>
          </button>

          {/* Expanded Details */}
          {expandedMethod === method?.id && (
            <div className="px-4 pb-4 border-t border-border bg-muted/30">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <h4 className="text-sm font-medium text-foreground mb-2">Features</h4>
                  <ul className="space-y-1">
                    {method?.features?.map((feature, index) => (
                      <li key={index} className="flex items-center space-x-2 text-sm text-muted-foreground">
                        <Icon name="Check" size={14} className="text-success" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div>
                  <h4 className="text-sm font-medium text-foreground mb-2">Limits</h4>
                  <div className="space-y-1 text-sm text-muted-foreground">
                    <div>Min: {formatAmount(method?.minAmount)}</div>
                    <div>Max: {formatAmount(method?.maxAmount)}</div>
                    <div>Processing: {method?.estimatedTime}</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      ))}
      {/* Security Notice */}
      <div className="flex items-start space-x-3 p-4 bg-success/10 border border-success/20 rounded-lg">
        <Icon name="Shield" size={20} className="text-success mt-0.5" />
        <div>
          <h4 className="font-medium text-success mb-1">Secure Payment</h4>
          <p className="text-sm text-muted-foreground">
            All transactions are encrypted and protected by SSL certification. 
            Your payment information is never stored on our servers.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentMethodSelector;